/** @format */

import { useState, useRef, useEffect, useCallback } from 'react';
import { HiSpeakerWave, HiStop } from 'react-icons/hi2';
import type { ReactNode } from 'react';
import buttonConfig from '../../config';
import TTSModal from '../ui/TTSModal';
import { useAuthStore } from '../../context/useAuthStore';

interface MyCustomTabsProps {
	tabs: { label: string; content: ReactNode }[];
	activeTab?: number;
	setActiveTab?: (index: number) => void;
}

const MyCustomTabs = ({
	tabs,
	activeTab: controlledTab,
	setActiveTab: setControlledTab,
}: MyCustomTabsProps) => {
	const [internalTab, setInternalTab] = useState<number>(0);
	const activeTab = controlledTab ?? internalTab;
	const setActiveTab = setControlledTab ?? setInternalTab;

	const [isReading, setIsReading] = useState<boolean>(false);
	const [isPaused, setIsPaused] = useState<boolean>(false);
	const [isPageTranslated, setIsPageTranslated] = useState<boolean>(false);
	const contentRef = useRef<HTMLDivElement>(null);
	const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
	const [isSpeechAvailable, setIsSpeechAvailable] = useState<boolean>(true);
	const { user } = useAuthStore();
	const [showModal, setShowModal] = useState(true);
	const [userName, setUserName] = useState<string>('');

	const globalTextRef = useRef<string>('');
	const charIndexRef = useRef<number>(0);
	const offsetRef = useRef<number>(0);
	const spansRef = useRef<HTMLSpanElement[] | null>(null);
	const pausedByUserRef = useRef<boolean>(false);
	const prevActiveTabRef = useRef<number | null>(null);
	const clearTimeoutRef = useRef<number | null>(null);
	const shouldReadNextTabRef = useRef<boolean>(false);
	const originalNodesRef = useRef<
		{ parent: Node; oldNode: Node; spans: HTMLElement[] }[]
	>([]);

	const detectPageLanguage = (): string => {
		const htmlLang = document.documentElement.lang;
		return htmlLang?.trim() || navigator.language || 'en-IN';
	};

	useEffect(() => {
		if (user) setUserName(user.fullName || 'Unknown User');
		else setUserName('User');
	}, [user]);

	const handleModalClose = () => setShowModal(false);

	useEffect(() => {
		const checkForTranslation = () => {
			const lang = detectPageLanguage().toLowerCase();
			const isEnglish = lang.startsWith('en');
			const hasGoogleTranslate =
				document.querySelector('html[translate="no"]') ||
				document.querySelector('.goog-te-banner') ||
				document.querySelector('.goog-te-combo');
			setIsPageTranslated(!!hasGoogleTranslate || !isEnglish);
		};

		checkForTranslation();

		const observer = new MutationObserver(checkForTranslation);
		observer.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ['lang', 'translate'],
			subtree: true,
		});

		const originalPushState = history.pushState;
		const originalReplaceState = history.replaceState;

		history.pushState = function (...args) {
			originalPushState.apply(this, args);
			setTimeout(checkForTranslation, 100);
		};

		history.replaceState = function (...args) {
			originalReplaceState.apply(this, args);
			setTimeout(checkForTranslation, 100);
		};

		window.addEventListener('popstate', checkForTranslation);

		return () => {
			observer.disconnect();
			try {
				history.pushState = originalPushState;
				history.replaceState = originalReplaceState;
			} catch {
				/* empty */
			}
			window.removeEventListener('popstate', checkForTranslation);
		};
	}, []);

	const isSpeechSynthesisAvailable = () =>
		typeof window !== 'undefined' &&
		'speechSynthesis' in window &&
		typeof SpeechSynthesisUtterance !== 'undefined';

	const getBestVoiceForLanguage = (
		lang: string
	): SpeechSynthesisVoice | null => {
		if (!isSpeechSynthesisAvailable()) return null;
		const voices = window.speechSynthesis.getVoices();
		if (!voices.length) return null;
		const exactMatch = voices.find((voice) => voice.lang === lang);
		if (exactMatch) return exactMatch;
		const langPrefix = lang.split('-')[0];
		const familyMatch = voices.find((voice) =>
			voice.lang.startsWith(langPrefix)
		);
		if (familyMatch) return familyMatch;
		return voices.find((voice) => voice.default) || voices[0];
	};

	const getTextFromContent = () => contentRef.current?.innerText || '';

	const isSkippableParent = (el: Element | null) => {
		if (!el) return true;
		const tag = el.tagName?.toLowerCase();
		return ['script', 'style', 'textarea', 'pre', 'code'].includes(tag || '');
	};

	const tokenize = (text: string) => {
		const tokens: string[] = [];
		let token = '';
		for (let i = 0; i < text.length; i++) {
			const ch = text[i];
			if (/\s/.test(ch)) {
				if (token.length > 0) {
					tokens.push(token);
					token = '';
				}
				let j = i;
				let ws = '';
				while (j < text.length && /\s/.test(text[j])) {
					ws += text[j];
					j++;
				}
				tokens.push(ws);
				i = j - 1;
			} else {
				token += ch;
			}
		}
		if (token.length > 0) tokens.push(token);
		return tokens;
	};

	const restoreDOM = () => {
		if (clearTimeoutRef.current) {
			window.clearTimeout(clearTimeoutRef.current);
			clearTimeoutRef.current = null;
		}

		if (!originalNodesRef.current.length) return;

		originalNodesRef.current
			.slice()
			.reverse()
			.forEach(({ parent, oldNode, spans }) => {
				if (parent && parent.isConnected) {
					const firstSpan = spans[0];
					if (firstSpan && parent.contains(firstSpan)) {
						parent.insertBefore(oldNode, firstSpan);
						spans.forEach((span) => {
							if (parent.contains(span)) {
								parent.removeChild(span);
							}
						});
					}
				}
			});
		originalNodesRef.current = [];
		spansRef.current = null;
		globalTextRef.current = '';
	};

	const wrapTextNodesWithSpans = () => {
		const root = contentRef.current;
		if (!root) return;

		if (originalNodesRef.current.length > 0) restoreDOM();

		const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
			acceptNode: (node: Node) => {
				const txt = node.nodeValue || '';
				if (!txt.trim()) return NodeFilter.FILTER_REJECT;
				const parent = node.parentElement;
				if (!parent) return NodeFilter.FILTER_REJECT;
				if (isSkippableParent(parent)) return NodeFilter.FILTER_REJECT;
				return NodeFilter.FILTER_ACCEPT;
			},
		} as any);

		const textNodes: Text[] = [];
		while (walker.nextNode()) {
			textNodes.push(walker.currentNode as Text);
		}

		const allSpans: HTMLSpanElement[] = [];
		const snapshot: { parent: Node; oldNode: Node; spans: HTMLElement[] }[] =
			[];
		let runningChar = 0;

		for (const tNode of textNodes) {
			const text = tNode.nodeValue || '';
			const tokens = tokenize(text);
			const frag = document.createDocumentFragment();
			const nodeSpans: HTMLElement[] = [];

			for (const tok of tokens) {
				const span = document.createElement('span');
				span.textContent = tok;
				span.setAttribute('data-char-start', String(runningChar));
				span.setAttribute(
					'data-char-end',
					String(runningChar + tok.length - 1)
				);
				span.style.whiteSpace = 'pre-wrap';
				span.style.display = 'inline';
				frag.appendChild(span);
				nodeSpans.push(span);
				allSpans.push(span);
				runningChar += tok.length;
			}

			const parent = tNode.parentNode;
			if (parent) {
				parent.replaceChild(frag, tNode);
				snapshot.push({ parent, oldNode: tNode, spans: nodeSpans });
			}
		}
		originalNodesRef.current = snapshot;
		spansRef.current = allSpans;
		globalTextRef.current = Array.from(allSpans)
			.map((s) => s.textContent || '')
			.join('');
	};

	const clearHighlightsAndRestore = () => {
		const spans = spansRef.current;
		if (!spans) return;
		spans.forEach((s) => s.classList.remove('highlighted-word'));
	};

	const highlightByCharIndex = (charIndex: number) => {
		const spans = spansRef.current;
		if (!spans || spans.length === 0) return;
		spans.forEach((s) => s.classList.remove('highlighted-word'));
		for (let i = 0; i < spans.length; i++) {
			const s = spans[i];
			const start = parseInt(s.getAttribute('data-char-start') || '0', 10);
			const end = parseInt(s.getAttribute('data-char-end') || '0', 10);
			if (charIndex >= start && charIndex <= end) {
				s.classList.add('highlighted-word');
				const rect = s.getBoundingClientRect();
				const viewportHeight =
					window.innerHeight || document.documentElement.clientHeight;
				const headerOffset = 120;
				const bottomOffset = 60;

				if (
					rect.top < headerOffset ||
					rect.bottom > viewportHeight - bottomOffset
				) {
					s.scrollIntoView({ behavior: 'smooth', block: 'center' });
				}
				break;
			}
		}
	};

	const stopReading = useCallback(() => {
		if (!isSpeechSynthesisAvailable()) {
			setIsReading(false);
			setIsPaused(false);
			return;
		}
		try {
			window.speechSynthesis.cancel();
		} catch {
			/* empty */
		}
		utteranceRef.current = null;
		pausedByUserRef.current = false;
		restoreDOM();

		setIsReading(false);
		setIsPaused(false);
		charIndexRef.current = 0;
		offsetRef.current = 0;
		if (clearTimeoutRef.current) {
			window.clearTimeout(clearTimeoutRef.current);
			clearTimeoutRef.current = null;
		}
	}, []);

	const pauseReading = () => {
		if (!isSpeechSynthesisAvailable()) return;
		try {
			if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
				window.speechSynthesis.pause();
				pausedByUserRef.current = true;
				setIsReading(false);
				setIsPaused(true);
			}
		} catch {
			/* empty */
		}
	};

	const resumeReading = () => {
		if (!isSpeechSynthesisAvailable()) return;
		try {
			if (window.speechSynthesis.paused) {
				if (window.speechSynthesis.speaking) {
					window.speechSynthesis.resume();
					pausedByUserRef.current = false;
					setIsReading(true);
					setIsPaused(false);
					return;
				} else {
					try {
						window.speechSynthesis.cancel();
					} catch {
						/* empty */
					}
					pausedByUserRef.current = false;
					setIsReading(false);
					setIsPaused(false);
				}
			}
		} catch {
			/* empty */
		}
		void startReading({ resumeIfNeeded: true });
	};

	const waitForNonEmptyContent = (timeout = 800, interval = 40) =>
		new Promise<string>((resolve) => {
			const start = performance.now();
			const check = () => {
				const txt = getTextFromContent();
				if (txt && txt.trim()) return resolve(txt);
				if (performance.now() - start > timeout) return resolve(txt);
				setTimeout(check, interval);
			};
			check();
		});

	const computeSegmentsFromSpans = (startChar = 0) => {
		const spans = spansRef.current || [];
		if (!spans.length) return [];
		const blockTags = new Set([
			'p',
			'div',
			'li',
			'section',
			'article',
			'header',
			'footer',
			'aside',
			'main',
			'h1',
			'h2',
			'h3',
			'h4',
			'h5',
			'h6',
			'blockquote',
			'pre',
		]);
		const segments: { text: string; start: number }[] = [];
		let runningText = '';
		let runningStart = -1;
		let prevContainer: Element | null = null;
		for (let i = 0; i < spans.length; i++) {
			const s = spans[i];
			const sText = s.textContent || '';
			const sStart = parseInt(s.getAttribute('data-char-start') || '0', 10);
			if (sStart < startChar) {
				continue;
			}
			const container = (() => {
				let el: Element | null = s.parentElement;
				while (el && !blockTags.has(el.tagName.toLowerCase())) {
					el = el.parentElement;
				}
				return el || s.parentElement;
			})();
			if (prevContainer === null) {
				prevContainer = container;
				runningStart = sStart;
				runningText = sText;
			} else if (container !== prevContainer) {
				segments.push({ text: runningText, start: runningStart });
				prevContainer = container;
				runningStart = sStart;
				runningText = sText;
			} else {
				runningText += sText;
			}
		}
		if (runningStart >= 0) {
			segments.push({ text: runningText, start: runningStart });
		}
		if (!segments.length && globalTextRef.current.slice(startChar).trim()) {
			segments.push({
				text: globalTextRef.current.slice(startChar),
				start: startChar,
			});
		}
		return segments;
	};

	const startReading = async (opts?: { resumeIfNeeded?: boolean }) => {
		if (!isSpeechSynthesisAvailable() || isPageTranslated) return;
		try {
			if (window.speechSynthesis.paused && !window.speechSynthesis.speaking) {
				try {
					window.speechSynthesis.cancel();
				} catch {
					/* empty */
				}
			}
		} catch {
			/* empty */
		}
		const fullTextCandidate = getTextFromContent();
		if (!fullTextCandidate?.trim()) {
			const waited = await waitForNonEmptyContent();
			if (!waited?.trim()) return;
		}
		if (window.speechSynthesis.speaking) return;
		wrapTextNodesWithSpans();
		const startChar =
			opts?.resumeIfNeeded && charIndexRef.current > 0
				? charIndexRef.current
				: 0;
		offsetRef.current = startChar;
		const textToSpeak = globalTextRef.current.slice(startChar);
		if (!textToSpeak) return;
		try {
			window.speechSynthesis.cancel();
		} catch {
			/* empty */
		}
		const lang = detectPageLanguage();
		const voice = getBestVoiceForLanguage(lang);
		const segments = computeSegmentsFromSpans(startChar);
		if (!segments.length) {
			const fallbackUtterance = new SpeechSynthesisUtterance(textToSpeak);
			fallbackUtterance.lang = lang;
			fallbackUtterance.rate = 1;
			fallbackUtterance.pitch = 1;
			if (voice) fallbackUtterance.voice = voice;
			fallbackUtterance.onboundary = (event: any) => {
				const charIndexAbsolute = (event.charIndex || 0) + offsetRef.current;
				charIndexRef.current = charIndexAbsolute;
				highlightByCharIndex(charIndexAbsolute);
			};
			fallbackUtterance.onstart = () => {
				utteranceRef.current = fallbackUtterance;
				setIsReading(true);
				setIsPaused(false);
				pausedByUserRef.current = false;
			};
			fallbackUtterance.onend = () => {
				utteranceRef.current = null;
				setIsReading(false);
				setIsPaused(false);
				charIndexRef.current = 0;
				offsetRef.current = 0;
				spansRef.current = null;
				if (clearTimeoutRef.current) {
					window.clearTimeout(clearTimeoutRef.current);
					clearTimeoutRef.current = null;
				}
				clearTimeoutRef.current = window.setTimeout(() => {
					clearHighlightsAndRestore();
				}, 1500);
			};
			try {
				utteranceRef.current = fallbackUtterance;
				window.speechSynthesis.speak(fallbackUtterance);
			} catch {
				utteranceRef.current = null;
				setIsReading(false);
				setIsPaused(false);
			}
			return;
		}

		const pauseMs = 600;
		let currentIndex = 0;
		let isCancelled = false;

		const speakSegment = (idx: number) => {
			if (isCancelled) return;
			if (idx >= segments.length) {
				if (activeTab < tabs.length - 1) {
					restoreDOM();
					shouldReadNextTabRef.current = true;
					setActiveTab(activeTab + 1);
				} else {
					utteranceRef.current = null;
					restoreDOM();
					setIsReading(false);
					setIsPaused(false);
					charIndexRef.current = 0;
					offsetRef.current = 0;
					if (clearTimeoutRef.current) {
						window.clearTimeout(clearTimeoutRef.current);
						clearTimeoutRef.current = null;
					}
				}
				return;
			}

			const seg = segments[idx];
			if (!seg.text || !seg.text.trim()) {
				currentIndex = idx + 1;
				offsetRef.current = seg.start + seg.text.length;
				window.setTimeout(() => speakSegment(currentIndex), 0);
				return;
			}

			const utter = new SpeechSynthesisUtterance(seg.text);
			utter.lang = lang;
			utter.rate = 1;
			utter.pitch = 1;
			if (voice) utter.voice = voice;

			utter.onboundary = (event: any) => {
				const charIndexInUtter = event.charIndex || 0;
				const charIndexAbsolute = seg.start + charIndexInUtter;
				charIndexRef.current = charIndexAbsolute;
				highlightByCharIndex(charIndexAbsolute);
			};

			utter.onstart = () => {
				utteranceRef.current = utter;
				setIsReading(true);
				setIsPaused(false);
				pausedByUserRef.current = false;
			};

			utter.onend = () => {
				utteranceRef.current = null;
				currentIndex = idx + 1;
				offsetRef.current = seg.start + seg.text.length;
				if (pausedByUserRef.current || isCancelled) {
					setIsReading(false);
					setIsPaused(pausedByUserRef.current);
					return;
				}
				window.setTimeout(() => {
					if (!isCancelled && !pausedByUserRef.current)
						speakSegment(currentIndex);
				}, pauseMs);
			};

			utter.onerror = () => {
				utteranceRef.current = null;
				isCancelled = true;
				setIsReading(false);
				setIsPaused(false);
			};

			utteranceRef.current = utter;
			try {
				window.speechSynthesis.speak(utter);
			} catch {
				utteranceRef.current = null;
				setIsReading(false);
				setIsPaused(false);
				isCancelled = true;
			}
		};

		try {
			window.speechSynthesis.cancel();
		} catch {
			/* empty */
		}

		speakSegment(0);
	};

	const tabClass = (index: number) =>
		`myCustomTab ${activeTab === index ? 'active' : ''}`;

	useEffect(() => {
		if (prevActiveTabRef.current === null) {
			prevActiveTabRef.current = activeTab;
			return;
		}
		if (prevActiveTabRef.current !== activeTab) {
			stopReading();
			prevActiveTabRef.current = activeTab;

			if (shouldReadNextTabRef.current) {
				shouldReadNextTabRef.current = false;
				setTimeout(() => {
					void startReading();
				}, 800);
			}
		}
	}, [activeTab, stopReading]);

	useEffect(() => {
		if (!isSpeechSynthesisAvailable()) {
			setIsSpeechAvailable(false);
			return;
		}
		const loadVoices = () => {
			window.speechSynthesis.getVoices();
			setIsSpeechAvailable(true);
		};
		window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
		loadVoices();
		return () => {
			window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
		};
	}, []);

	useEffect(() => {
		const handleBeforeUnload = () => stopReading();
		const handleVisibilityChange = () => {
			if (document.hidden) stopReading();
		};
		window.addEventListener('beforeunload', handleBeforeUnload);
		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => {
			stopReading();
			window.removeEventListener('beforeunload', handleBeforeUnload);
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, [stopReading]);

	const isReadAloudDisabled = !isSpeechSynthesisAvailable() || isPageTranslated;
	const isDisabled = !isReading && isReadAloudDisabled;

	return (
		<div className='myCustomTabsWrapper'>
			<style>{`.highlighted-word{background:rgba(253,218,13);transition:background 120ms ease-in;}`}</style>
			<div className='myCustomTabContainer no-scrollbar'>
				<div className='max-w-5xl mx-auto flex items-center justify-between space-x-4'>
					<div className='flex items-center gap-2'>
						{tabs.map((tab, index) => (
							<button
								key={index}
								type='button'
								onClick={() => {
									restoreDOM();
									setActiveTab(index);
								}}
								className={tabClass(index)}>
								{tab.label}
							</button>
						))}
					</div>
					{buttonConfig.showReadAloudButton && (
						<div className='flex gap-2'>
							<button
								type='button'
								onClick={() => {
									if (isReading) {
										pauseReading();
									} else if (isPaused) {
										resumeReading();
									} else {
										void startReading();
									}
								}}
								disabled={isDisabled}
								className={`cursor-pointer border sm:font-semibold font-medium sm:py-2.5 py-1 capitalize sm:px-5 px-2.5 rounded-md transition-all duration-200 flex items-center md:gap-3 sm:gap-2 gap-1 text-xs md:text-base
                ${
									isReading
										? 'hover:bg-red-500 hover:text-white text-red-500 border-red-500 '
										: ''
								}
                ${
									!isReading && !isDisabled
										? 'hover:bg-myButtonBg hover:text-myButtonText text-myButtonBg border-myButtonBg'
										: ''
								}
                ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
								title={
									isPageTranslated
										? 'Text-to-speech only works in English'
										: isDisabled
										? 'Text-to-speech not available'
										: isReading
										? 'Pause reading'
										: isPaused
										? 'Resume reading'
										: 'Start reading aloud'
								}>
								{isReading ? (
									<HiStop className='md:w-5 md:h-5 w-4 h-4' />
								) : (
									<HiSpeakerWave className='md:w-5 md:h-5 w-4 h-4' />
								)}
								<span>
									{isReading ? 'Pause' : isPaused ? 'Resume' : 'Start Reading'}
								</span>
							</button>
						</div>
					)}
				</div>
			</div>
			<div
				ref={contentRef}
				className='tab-content px-4'>
				{tabs[activeTab]?.content}
			</div>
			{!isSpeechAvailable && (
				<TTSModal
					userName={userName}
					showModal={showModal}
					onClose={handleModalClose}
				/>
			)}
		</div>
	);
};

export default MyCustomTabs;
